import { supabase } from './supabaseClient';
import type { PeriodTagData, TagData, PeriodSlugDetails } from '@/components/reports/tags/types';

export interface FetchTagsDataRequest {
  tagId: string;
  tagName: string;
  periods: string[]; // Array of quarters like ["2025Q4", "2025Q3"]
  totalPages: number; // Total pages from pages/slugs API
}

/**
 * Fetches tag data from app_tags_map database for specified tag and periods.
 * Returns counts of detected, not detected, and unknown (calculated as delta) per period.
 * Also calculates unique slug counts across all periods for summary stats.
 */
export async function fetchTagsData(
  request: FetchTagsDataRequest
): Promise<TagData> {
  const { tagId, tagName, periods, totalPages } = request;

  try {
    // Fetch all records for the tag and requested periods (including slug for uniqueness)
    const { data: records, error } = await supabase
      .from('app_tags_map')
      .select('quarter, slug, tag_applies')
      .eq('tag_id', tagId)
      .in('quarter', periods)
      .not('tag_applies', 'is', null);

    if (error) {
      console.error('Supabase query error:', error);
      throw new Error(`Failed to fetch tag data: ${error.message}`);
    }

    // Group by period and track slugs per period
    const periodMap = new Map<string, {
      detected: number;
      not_detected: number;
      taggedSlugs: Set<string>;
    }>();

    // Initialize all periods with zero counts
    periods.forEach(period => {
      periodMap.set(period, {
        detected: 0,
        not_detected: 0,
        taggedSlugs: new Set()
      });
    });

    // Track unique slugs across ALL periods
    const slugsEverTagged = new Set<string>();
    const slugsEverNotTagged = new Set<string>();

    // Count tag statuses per period AND track unique slugs
    records?.forEach(record => {
      const counts = periodMap.get(record.quarter);
      if (counts) {
        if (record.tag_applies === true) {
          counts.detected++;
          counts.taggedSlugs.add(record.slug);
          slugsEverTagged.add(record.slug);
        } else if (record.tag_applies === false) {
          counts.not_detected++;
          slugsEverNotTagged.add(record.slug);
        }
      }
    });

    // Calculate unique counts for summary
    const uniqueTagged = slugsEverTagged.size;

    // Only count as "not tagged" if NEVER tagged
    const uniqueNotTagged = Array.from(slugsEverNotTagged)
      .filter(slug => !slugsEverTagged.has(slug))
      .length;

    const uniqueUnknown = totalPages - uniqueTagged - uniqueNotTagged;

    // Sort periods chronologically (newest first) for display
    const sortedPeriods = [...periods].sort((a, b) => {
      const [yearA, qA] = a.split('Q').map(Number);
      const [yearB, qB] = b.split('Q').map(Number);
      return yearA === yearB ? qB - qA : yearB - yearA;
    });

    // Transform into PeriodTagData array, calculating unknown and changes
    const periodData: PeriodTagData[] = sortedPeriods.map((period, index) => {
      const counts = periodMap.get(period) || {
        detected: 0,
        not_detected: 0,
        taggedSlugs: new Set()
      };
      const unknown = totalPages - counts.detected - counts.not_detected;

      // Calculate lost and gained tags by comparing with previous period
      // Since sorted newest first, previous period is at index + 1
      let lostTag = 0;
      let gainedTag = 0;

      if (index < sortedPeriods.length - 1) {
        const previousPeriod = sortedPeriods[index + 1];
        const previousCounts = periodMap.get(previousPeriod);

        if (previousCounts) {
          // Lost: slugs that were tagged in previous but not in current
          lostTag = Array.from(previousCounts.taggedSlugs)
            .filter(slug => !counts.taggedSlugs.has(slug))
            .length;

          // Gained: slugs that are tagged in current but weren't in previous
          gainedTag = Array.from(counts.taggedSlugs)
            .filter(slug => !previousCounts.taggedSlugs.has(slug))
            .length;
        }
      }

      return {
        period,
        detected: counts.detected,
        not_detected: counts.not_detected,
        unknown: unknown > 0 ? unknown : 0,
        total: totalPages,
        lostTag,
        gainedTag
      };
    });

    return {
      tagId,
      tagName,
      periods: periodData,
      summary: {
        uniqueTagged,
        uniqueNotTagged,
        uniqueUnknown: uniqueUnknown > 0 ? uniqueUnknown : 0,
        totalPages
      }
    };
  } catch (error) {
    console.error('Error fetching tag data:', error);
    throw error;
  }
}

/**
 * Fetches detailed slug-level tag data for a specific period.
 * Returns all slugs with their tag status, reason, and image URLs.
 * Supports filtering by "all", "new", or "lost" slugs.
 */
export async function fetchPeriodSlugDetails(
  tagId: string,
  period: string,
  filter: 'all' | 'new' | 'lost' = 'all',
  sortedPeriods?: string[]
): Promise<PeriodSlugDetails> {
  try {
    // Fetch all records for the tag and period where tag was detected
    const { data: records, error } = await supabase
      .from('app_tags_map')
      .select('slug, tag_applies, reason, annotated_url, screenshot_url, confidence')
      .eq('tag_id', tagId)
      .eq('quarter', period)
      .eq('tag_applies', true)
      .order('slug', { ascending: true });

    if (error) {
      console.error('Supabase query error:', error);
      throw new Error(`Failed to fetch period slug details: ${error.message}`);
    }

    let filteredRecords = records || [];

    // If filtering by "new" or "lost", we need the previous period's data
    if ((filter === 'new' || filter === 'lost') && sortedPeriods) {
      const periodIndex = sortedPeriods.indexOf(period);
      const previousPeriod = periodIndex < sortedPeriods.length - 1 ? sortedPeriods[periodIndex + 1] : null;

      if (previousPeriod) {
        // Fetch previous period's tagged slugs
        const { data: previousRecords } = await supabase
          .from('app_tags_map')
          .select('slug')
          .eq('tag_id', tagId)
          .eq('quarter', previousPeriod)
          .eq('tag_applies', true);

        const previousSlugs = new Set((previousRecords || []).map(r => r.slug));
        const currentSlugs = new Set((records || []).map(r => r.slug));

        if (filter === 'new') {
          // New: tagged in current but not in previous
          filteredRecords = (records || []).filter(r => !previousSlugs.has(r.slug));
        } else if (filter === 'lost') {
          // Lost: tagged in previous but not in current
          // Need to fetch details for these slugs from previous period
          const lostSlugs = Array.from(previousSlugs).filter(slug => !currentSlugs.has(slug));

          if (lostSlugs.length > 0) {
            const { data: lostRecords } = await supabase
              .from('app_tags_map')
              .select('slug, tag_applies, reason, annotated_url, screenshot_url, confidence')
              .eq('tag_id', tagId)
              .eq('quarter', previousPeriod)
              .in('slug', lostSlugs)
              .order('slug', { ascending: true });

            filteredRecords = lostRecords || [];
          } else {
            filteredRecords = [];
          }
        }
      }
    }

    return {
      period,
      tagId,
      slugs: filteredRecords
    };
  } catch (error) {
    console.error('Error fetching period slug details:', error);
    throw error;
  }
}
